import { ERC721InterfaceId, ERC1155InterfaceId } from '@mogul/shared';
import type { BigNumberish } from 'ethers';

import type { MogulNFTERC721 } from '../lib/contracts';
import { MogulNFTERC721Factory } from '../lib/contracts';
import { NFT_ERC_721_CONTRACT_ADDRESS } from '../lib/smart-contract-utils';
import { useWeb3State } from '.';
import { useContract } from './contract.hooks';

export const useNFTErc721Contract = (props = { nftContractAddress: '' }) => {
  const { walletDetails } = useWeb3State();
  const { contract } = useContract<MogulNFTERC721>(
    MogulNFTERC721Factory,
    NFT_ERC_721_CONTRACT_ADDRESS,
    props.nftContractAddress,
  );

  const balanceOf = async () => {
    return contract?.balanceOf(walletDetails.address ?? '');
  };

  const ownerOf = async (tokenId: string) => {
    return contract?.ownerOf(tokenId);
  };

  const getNftApprovalStatus = async (account: string, operator: string) => {
    if (account === '' || operator === '') {
      return false;
    }

    const approvalStatus = await contract?.isApprovedForAll(account, operator);

    return approvalStatus ?? false;
  };

  const setApprovalForAll = async (operator: string, approved: boolean) => {
    try {
      if (!contract) {
        return false;
      }
      const trx = await contract.setApprovalForAll(operator, approved);
      await trx.wait();

      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'nft-contract.hooks.ts', function: 'setApprovalForAll' },
        contexts: {},
      });
    }
    return false;
  };

  const isApprovedForAll = async (owner: string, operator: string) => {
    try {
      if (!contract) {
        return false;
      }

      return await contract?.isApprovedForAll(owner, operator);
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'nft-contract.hooks.ts', function: 'isApprovedForAll' },
        contexts: {},
      });
    }
  };

  const transferNft = async (from: string, to: string, id: BigNumberish) => {
    try {
      if (!contract) {
        return false;
      }

      const trx = await contract.transferFrom(from, to, id);
      await trx.wait();

      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'nft-contract.hooks.ts', function: 'approveAnyContract' },
        contexts: {},
      });
    }
    return false;
  };

  const mintNft = async (
    recipientAddress: string,
    tokenID: BigNumberish,
    URI: string,
  ) => {
    try {
      if (!contract) {
        return false;
      }

      const mint = await contract.mintToken(recipientAddress, tokenID, URI);

      await mint.wait();

      return true;
    } catch (e) {
      console.error('minting failed', e);
      window.Sentry?.captureException(e, {
        tags: {
          file: 'nft-erc-721-contract.hooks.ts',
          function: 'mintingFailed',
        },
        contexts: {},
      });
    }

    return false;
  };

  const isErc1155 = async () => {
    if (!contract) {
      return false;
    }

    return contract.supportsInterface(ERC1155InterfaceId);
  };

  const isErc721 = async () => {
    if (!contract) {
      return false;
    }

    return contract.supportsInterface(ERC721InterfaceId);
  };

  return {
    initialized: contract !== undefined,
    setApprovalForAll,
    isApprovedForAll,
    getNftApprovalStatus,
    transferNft,
    balanceOf,
    ownerOf,
    mintNft,
    isErc721,
    isErc1155,
  };
};
